import React, { useState, useEffect, useMemo } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Variable, Hash, Calendar, List, Type, Plus, Trash2, GripVertical } from 'lucide-react';
import { extractVariables, buildDefinitions } from './variableEngine';

const VARIABLE_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'dropdown', label: 'Dropdown', icon: List },
];

export default function VariableDefinitionEditor({ sqlTemplate, definitions = [], onChange }) {
  const extracted = useMemo(() => extractVariables(sqlTemplate || ''), [sqlTemplate]);

  const [defs, setDefs] = useState(() => {
    if (definitions.length > 0) return definitions;
    return buildDefinitions(extracted);
  });

  // Re-sync when SQL changes
  useEffect(() => {
    const currentNames = new Set(defs.map(d => d.name));
    const extractedNames = new Set(extracted.map(v => v.name));
    let updated = [...defs];
    let changed = false;

    for (const v of extracted) {
      if (!currentNames.has(v.name)) {
        updated.push({
          name: v.name, type: v.type || 'text',
          label: v.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          defaultValue: v.defaultValue || null, options: null, required: true,
          placeholder: null, min: null, max: null,
        });
        changed = true;
      }
    }
    const beforeCount = updated.length;
    updated = updated.filter(d => extractedNames.has(d.name));
    if (updated.length !== beforeCount) changed = true;

    if (changed) { setDefs(updated); onChange(updated); }
  }, [extracted]);

  const updateDef = (index, field, value) => {
    const next = [...defs];
    next[index] = { ...next[index], [field]: value };
    setDefs(next);
    onChange(next);
  };

  const addOption = (index) => {
    const next = [...defs];
    next[index] = { ...next[index], options: [...(next[index].options || []), ''] };
    setDefs(next);
    onChange(next);
  };

  const updateOption = (di, oi, value) => {
    const next = [...defs];
    const opts = [...(next[di].options || [])];
    opts[oi] = value;
    next[di] = { ...next[di], options: opts };
    setDefs(next);
    onChange(next);
  };

  const removeOption = (di, oi) => {
    const next = [...defs];
    const opts = [...(next[di].options || [])];
    opts.splice(oi, 1);
    next[di] = { ...next[di], options: opts };
    setDefs(next);
    onChange(next);
  };

  if (extracted.length === 0) {
    return (
      <div className="p-3 bg-slate-800/30 rounded-lg border border-dashed border-slate-700 text-center">
        <Variable className="w-5 h-5 text-slate-500 mx-auto mb-1" />
        <p className="text-xs text-slate-500">
          No variables detected. Use <code className="text-amber-400/70">{'{{variable_name}}'}</code> syntax in your SQL.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Variable className="w-4 h-4 text-amber-400" />
        <span className="text-xs font-semibold text-slate-200">Variable Definitions</span>
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-600 text-slate-400">
          {extracted.length} detected
        </Badge>
      </div>

      {defs.map((def, idx) => (
        <div key={def.name} className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50 space-y-2">
          {/* Header */}
          <div className="flex items-center justify-between">
            <code className="text-xs font-mono text-amber-300 bg-amber-400/10 px-1.5 py-0.5 rounded">
              {`{{${def.name}}}`}
            </code>
            <Select value={def.type} onValueChange={(val) => updateDef(idx, 'type', val)}>
              <SelectTrigger className="h-7 w-32 text-xs bg-slate-900/50 border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VARIABLE_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="flex items-center gap-1.5"><t.icon className="w-3 h-3" />{t.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Label + Default */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-500">Display Label</Label>
              <Input data-testid="sentinel-text-10px-text-slate-500-input" value={def.label || ''} onChange={(e) => updateDef(idx, 'label', e.target.value)}
                placeholder="e.g. Record Limit" className="h-7 text-xs bg-slate-900/50 border-slate-600" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-500">Default Value</Label>
              <Input data-testid="sentinel-text-10px-text-slate-500-input-1" value={def.defaultValue || ''} onChange={(e) => updateDef(idx, 'defaultValue', e.target.value)}
                placeholder="e.g. 10" className="h-7 text-xs bg-slate-900/50 border-slate-600" />
            </div>
          </div>

          {/* Number: min/max */}
          {def.type === 'number' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-500">Min</Label>
                <Input data-testid="sentinel-no-min-input" type="number" value={def.min ?? ''} placeholder="No min"
                  onChange={(e) => updateDef(idx, 'min', e.target.value ? Number(e.target.value) : null)}
                  className="h-7 text-xs bg-slate-900/50 border-slate-600" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-500">Max</Label>
                <Input data-testid="sentinel-no-max-input" type="number" value={def.max ?? ''} placeholder="No max"
                  onChange={(e) => updateDef(idx, 'max', e.target.value ? Number(e.target.value) : null)}
                  className="h-7 text-xs bg-slate-900/50 border-slate-600" />
              </div>
            </div>
          )}

          {/* Dropdown: options */}
          {def.type === 'dropdown' && (
            <div className="space-y-1.5">
              <Label className="text-[10px] text-slate-500">Dropdown Options</Label>
              {(def.options || []).map((opt, oi) => (
                <div key={oi} className="flex items-center gap-1.5">
                  <GripVertical className="w-3 h-3 text-slate-600 flex-shrink-0" />
                  <Input data-testid="sentinel-variabledefinitioneditor-input" value={typeof opt === 'object' ? opt.value : opt}
                    onChange={(e) => updateOption(idx, oi, e.target.value)}
                    placeholder={`Option ${oi + 1}`} className="h-7 text-xs bg-slate-900/50 border-slate-600 flex-1" />
                  <Button size="sm" variant="ghost" onClick={() => removeOption(idx, oi)}
                    className="h-7 w-7 p-0 text-slate-500 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <Button data-testid="sentinel-add-option-btn" size="sm" variant="ghost" onClick={() => addOption(idx)}
                className="h-6 px-2 text-[10px] text-slate-400 hover:text-amber-400">
                <Plus className="w-3 h-3 mr-1" /> Add Option
              </Button>
            </div>
          )}

          {/* Required toggle */}
          <div className="flex items-center justify-between pt-1 border-t border-slate-800/50">
            <Label className="text-[10px] text-slate-500">Required</Label>
            <Switch checked={def.required !== false} onCheckedChange={(val) => updateDef(idx, 'required', val)} className="scale-75" />
          </div>
        </div>
      ))}
    </div>
  );
}